import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { searchEverything, type GlobalSearchResult } from "@/services/api";
import { colors, semanticColors, layout, heights, spacing, radius, typography, iconSize, iconStroke, shadows } from "@/styles/tokens";

// The top-bar search (global-search, 2026-09-30, D144). READ-ONLY: it lists
// what three existing reads return and navigates; it writes nothing and
// prices nothing.
//
// SCOPING IS THE SERVER'S. Quotes come from the same two routes the quote list
// uses (finalised for everyone who may read quotes, drafts scoped to the
// session user by the server), customers and products from their own list
// routes. This component adds no filter and no widening -- a Sales user sees
// exactly the quotes /quotes would show them, a Viewer no drafts.
//
// A group that FAILED says so (result.failed). A search that silently
// returned two of the three kinds of thing would read as "nothing found".

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const PER_GROUP = 6;

export function GlobalSearch() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GlobalSearchResult | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  // Last response wins: a slow answer for "ab" must not overwrite "abc".
  const token = useRef(0);

  const query = text.trim();

  useEffect(() => {
    if (query.length < MIN_CHARS) {
      token.current += 1;
      setResult(null);
      setBusy(false);
      return undefined;
    }
    const mine = ++token.current;
    setBusy(true);
    const timer = window.setTimeout(async () => {
      const out = await searchEverything(query, PER_GROUP);
      if (mine !== token.current) return;
      setResult(out);
      setBusy(false);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const go = (to: string) => {
    setOpen(false);
    setText("");
    setResult(null);
    navigate(to);
  };

  const found = result
    ? result.quotes.length + result.customers.length + result.products.length
    : 0;
  const showPanel = open && query.length >= MIN_CHARS;
  const enc = encodeURIComponent;

  const heading = (label: string, testid: string) => (
    <div
      data-testid={testid}
      style={{
        padding: `${spacing[6]}px ${spacing[12]}px`, fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.medium, color: semanticColors.textMuted,
        textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
      }}
    >
      {label}
    </div>
  );

  const rowStyle = {
    display: "block", width: "100%", textAlign: "left" as const,
    padding: `${spacing[6]}px ${spacing[12]}px`, background: "transparent", border: "none",
    cursor: "pointer", fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  };
  const subStyle = { fontSize: typography.fontSize.xs, color: semanticColors.textSecondary };

  return (
    <div ref={boxRef} style={{ position: "relative", width: layout.topBarSearchWidth, maxWidth: "100%" }}>
      <div
        className="flex items-center"
        style={{
          height: heights.avatarSearch, backgroundColor: colors.white, gap: spacing[8],
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          padding: `0 ${spacing[8]}px`,
        }}
      >
        <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
        <input
          data-testid="app-search"
          type="search"
          aria-label="Search quotes, customers and products"
          placeholder="Search quotes, customers, products"
          value={text}
          maxLength={80}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
          style={{
            flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
            fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
          }}
        />
      </div>

      {showPanel && (
        <div
          data-testid="app-search-results"
          style={{
            position: "absolute", top: heights.avatarSearch + spacing[4], left: 0, right: 0,
            zIndex: 50, maxHeight: "70vh", overflowY: "auto", backgroundColor: colors.white,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            boxShadow: shadows.modal, paddingBottom: spacing[4],
          }}
        >
          {busy && !result && (
            <div data-testid="app-search-busy" style={{ ...subStyle, padding: spacing[12] }}>Searching…</div>
          )}

          {result && result.failed.length > 0 && (
            <div
              data-testid="app-search-failed"
              role="alert"
              style={{
                padding: `${spacing[6]}px ${spacing[12]}px`, fontSize: typography.fontSize.xs,
                color: semanticColors.destructive,
              }}
            >
              {result.failed.map((f) => `${f.what} could not be searched. ${f.error}`).join(" ")}
            </div>
          )}

          {result && found === 0 && result.failed.length === 0 && !busy && (
            <div data-testid="app-search-empty" style={{ ...subStyle, padding: spacing[12] }}>
              Nothing matched “{query}”.
            </div>
          )}

          {result && result.quotes.length > 0 && (
            <div>
              {heading("Quotes", "app-search-group-quotes")}
              {result.quotes.map((q) => (
                <button
                  key={q.quote_id} type="button" style={rowStyle}
                  data-testid={`app-search-quote-${q.quote_id}`}
                  onClick={() => go(`/saved-quotes/${q.quote_id}`)}
                >
                  <div>
                    {q.name}{q.is_draft ? " (draft)" : ""}
                  </div>
                  <div style={subStyle}>
                    {[q.partner_name, q.project_ref, q.project_name].filter(Boolean).join(" · ") || "—"}
                  </div>
                </button>
              ))}
              <button
                type="button" style={{ ...rowStyle, color: semanticColors.textSecondary }}
                data-testid="app-search-all-quotes"
                onClick={() => go(`/quotes?q=${enc(query)}`)}
              >
                All quotes matching “{query}”
              </button>
            </div>
          )}

          {result && result.customers.length > 0 && (
            <div>
              {heading("Customers", "app-search-group-customers")}
              {result.customers.map((c) => (
                <button
                  key={c.id} type="button" style={rowStyle}
                  data-testid={`app-search-customer-${c.id}`}
                  onClick={() => go(`/quotes?q=${enc(c.name)}`)}
                >
                  <div>{c.name}</div>
                  {c.vat ? <div style={subStyle}>GSTIN {c.vat}</div> : null}
                </button>
              ))}
            </div>
          )}

          {result && result.products.length > 0 && (
            <div>
              {heading("Products", "app-search-group-products")}
              {result.products.map((p) => (
                <button
                  key={p.id} type="button" style={rowStyle}
                  data-testid={`app-search-product-${p.id}`}
                  onClick={() => go(`/catalog?q=${enc(p.partNo)}`)}
                >
                  <div>{p.partNo}</div>
                  <div style={subStyle}>{p.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
